<template>
  <time :datetime="iso" data-el="datetime">{{ f }}</time>
</template>

<script setup lang="ts">
import { computed } from 'vue';

const props = defineProps<{
  value?: string | Date | null | undefined;
  precision?: 'day' | 'hours' | 'seconds' | 'milliseconds' | null | undefined;
}>();

const parseValue = (val: string | Date | null | undefined): Date | null => {
  if (!val) return null;
  try {
    return typeof val === 'string' ? new Date(val) : val;
  } catch {
    return null;
  }
};

const safeFormatDate = (
  date: Date,
  options: Intl.DateTimeFormatOptions,
): string => {
  try {
    return new Intl.DateTimeFormat(undefined, options).format(date);
  } catch {
    return 'N/A';
  }
};

const iso = computed(() => {
  const parsedDate = parseValue(props.value);
  try {
    return parsedDate ? parsedDate.toISOString() : undefined;
  } catch (e) {
    return undefined;
  }
});

const f = computed(() => {
  const parsedDate = parseValue(props.value);
  if (!parsedDate) return 'N/A';

  const now = new Date();
  const diffInMs = now.getTime() - parsedDate.getTime();

  const effectivePrecision =
    props.precision || (diffInMs > 24 * 60 * 60 * 1000 ? 'day' : 'seconds');

  try {
    switch (effectivePrecision) {
      case 'milliseconds':
        return safeFormatDate(parsedDate, {
          dateStyle: 'medium',
          timeStyle: 'long',
        });
      case 'seconds':
        return safeFormatDate(parsedDate, {
          dateStyle: 'medium',
          timeStyle: 'medium',
        });
      case 'hours':
        return safeFormatDate(parsedDate, {
          dateStyle: 'medium',
          timeStyle: 'short',
        });
      case 'day':
      default:
        return safeFormatDate(parsedDate, { dateStyle: 'medium' });
    }
  } catch {
    return 'N/A';
  }
});
</script>
